// features/automationScripts/utils/formatJsonLoose.js
//
// Indenting a JSON body that is not valid JSON.
//
// The executor cuts long response bodies off mid-token and appends its own
// marker, so the body that reaches the UI cannot be parsed. JSON.parse throws,
// and a strict formatter has nothing to fall back on but printing the whole
// thing as one unbroken line — which is the least readable moment to give up,
// because a truncated body is long by definition.
//
// This walks the text instead of parsing it, breaking lines on the structure it
// can see and leaving everything it cannot alone. Nothing is added or removed
// except whitespace between tokens: the marker, an unterminated string and any
// trailing garbage all survive verbatim.

const WHITESPACE = new Set([' ', '\t', '\n', '\r']);
const OPENERS = { '{': '}', '[': ']' };
const CLOSERS = new Set(['}', ']']);

/** The next character that is not whitespace, or '' at the end of the text. */
const nextMeaningful = (text, from) => {
  for (let i = from; i < text.length; i += 1) {
    if (!WHITESPACE.has(text[i])) return text[i];
  }
  return '';
};

/**
 * Indent JSON-shaped text without requiring it to be valid.
 *
 * @param {string} raw     the body as it arrived
 * @param {number} indent  spaces per level
 * @returns {string}       the same content, laid out over lines
 */
export const formatJsonLoose = (raw, indent = 2) => {
  if (typeof raw !== 'string' || raw === '') return raw;

  const pad = (depth) => ' '.repeat(Math.max(0, depth) * indent);

  let out = '';
  let depth = 0;
  let inString = false;
  let escaped = false;

  for (let i = 0; i < raw.length; i += 1) {
    const ch = raw[i];

    // Inside a string literal nothing is structure — braces, commas and the
    // executor's marker are all just characters, and an unterminated string
    // simply runs to the end of the text.
    if (inString) {
      out += ch;
      if (escaped) escaped = false;
      else if (ch === '\\') escaped = true;
      else if (ch === '"') inString = false;
      continue;
    }

    if (ch === '"') {
      inString = true;
      out += ch;
      continue;
    }

    // Whitespace between tokens is ours to decide, so the result reads the
    // same whether the body arrived compact or already spaced.
    if (WHITESPACE.has(ch)) continue;

    if (OPENERS[ch]) {
      // An empty object or array stays on one line rather than becoming a
      // brace, a blank line and a brace.
      if (nextMeaningful(raw, i + 1) === OPENERS[ch]) {
        out += ch;
        continue;
      }
      depth += 1;
      out += `${ch}\n${pad(depth)}`;
      continue;
    }

    if (CLOSERS.has(ch)) {
      // An empty container never raised the depth, so it must not lower it.
      if (out.endsWith('{') || out.endsWith('[')) {
        out += ch;
      } else {
        depth -= 1;
        out += `\n${pad(depth)}${ch}`;
      }
      continue;
    }

    if (ch === ',') {
      out += `,\n${pad(depth)}`;
      continue;
    }

    if (ch === ':') {
      out += ': ';
      continue;
    }

    out += ch;
  }

  return out;
};

export default formatJsonLoose;
